import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ConnectionTestAlert } from '@/components/common/ConnectionTestAlert';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Copy, Check, Info, CheckCircle2, AlertCircle, Pencil, PlugZap, Wand2, AlertTriangle, Tags } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { EditTargetDialog } from './EditTargetDialog';
import { ChangeTablePrefixDialog } from './ChangeTablePrefixDialog';
import { CardHeaderActions, type ActionItem } from '@/components/common/CardHeaderActions';
import { FirewallHelpDialog } from '@/components/common/FirewallHelpDialog';
import { usePostTargetsByIdTestConnection, useGetSchedules } from '@/api/control/control-plane/control-plane';
import { useError } from '@/contexts/ErrorContext';
import type {
  TargetDto,
  AgentDto,
  ProfileDto,
  ScheduleDto,
  SourceDto,
} from '@/api/control/missionImpactAPI.schemas';
import { calculateNextSyncTime, formatNextSyncTime } from '@/utils/nextSyncTime';
import { isVersionOutdated } from '@/services/versionUtils';
import { AgentHealthStatus } from '@/components/agents/AgentHealthStatus';

/**
 * Props for the TargetCard component
 */
interface TargetCardProps {
  /** Organization ID */
  orgId: string;
  /** Profile ID */
  profileId: string;
  /** Target data from profile (null if not configured) */
  target: TargetDto | null | undefined;
  /** Callback when connection test succeeds (to refresh data) */
  onTestSuccess?: () => void;
  /** URL to resume the setup wizard when target is missing */
  resumeSetupUrl?: string;
  /** Whether the profile is enabled */
  isProfileEnabled?: boolean;
  /** Agent assigned to this target (if any) */
  agent?: AgentDto | null;
  /** Latest available agent version from the manifest */
  latestVersion?: string | null;
  /** Region ID for the profile, used to display NAT IP in firewall help */
  regionId?: string | null;
  /** The profile's source, used to re-validate tables when changing the table prefix. */
  source?: SourceDto | null;
  /** Whether the profile has already triggered its initial sync (prefix change is destructive if so). */
  hasTriggeredInitialSync?: boolean;
  /** The profile's burst expiry (ISO-8601 UTC), or null when no burst is active. */
  burstExpiresUtc?: string | null;
}

/**
 * Card component for displaying a single target
 */
export function TargetCard({ orgId, profileId, target, onTestSuccess, resumeSetupUrl, isProfileEnabled, agent, latestVersion, regionId, source, hasTriggeredInitialSync, burstExpiresUtc }: TargetCardProps) {
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [prefixDialogOpen, setPrefixDialogOpen] = useState(false);
  const [firewallDialogOpen, setFirewallDialogOpen] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [schedules, setSchedules] = useState<ScheduleDto[]>([]);

  // Fetch schedules
  const { data: schedulesData } = useGetSchedules(orgId, profileId, {
    query: {
      enabled: !!orgId && !!profileId && !!target,
      staleTime: 1000 * 60 * 5,
    },
  });

  // Update schedules when data is loaded
  useEffect(() => {
    if (schedulesData?.data) {
      setSchedules(schedulesData.data);
    }
  }, [schedulesData]);

  const { mutate: testConnection, isPending: isTesting } = usePostTargetsByIdTestConnection({
    mutation: {
      onSuccess: () => {
        setConnectionTestResult({ success: true, message: t.profiles.targetCard.connectionSuccessful });
        toast.success(t.profiles.targetCard.testSuccessful);
        onTestSuccess?.();
      },
      onError: (error: unknown) => {
        const { message, errorCode } = handleApiError(
          error,
          { context: 'TargetCard.testConnection', profile: { regionId } as ProfileDto },
          { showToast: false },
        );

        if (errorCode === 'AzureSqlFirewallBlocked') {
          setFirewallDialogOpen(true);
        }

        setConnectionTestResult({ success: false, message });
      },
    },
  });

  const handleCopy = (text: string, fieldName: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedField(fieldName);
        toast.success(t.profiles.targetCard.copiedToClipboard);
        setTimeout(() => { setCopiedField(null); }, 2000);
      })
      .catch(() => {
        toast.error(t.profiles.targetCard.failedToCopy);
      });
  };

  const handleTestConnection = () => {
    if (!target?.id) return;
    setConnectionTestResult(null);
    testConnection({ orgId, profileId, targetId: target.id });
  };

  // Calculate next sync time (burst-aware: reflects the 5-minute cadence while a burst is active)
  const nextSyncTime = calculateNextSyncTime(schedules, new Date(), burstExpiresUtc ?? null);

  // Build actions based on target state
  const actions: ActionItem[] = target
    ? [
        {
          key: 'test',
          label: t.profiles.targetCard.testConnection,
          icon: PlugZap,
          onClick: handleTestConnection,
          isLoading: isTesting,
          tooltip: t.profiles.targetCard.testConnection,
        },
        {
          key: 'edit',
          label: t.targets.editDialog.buttonText,
          icon: Pencil,
          onClick: () => { setEditDialogOpen(true); },
          tooltip: t.targets.editDialog.buttonText,
        },
        {
          key: 'prefix',
          label: t.targets.prefixDialog.buttonText,
          icon: Tags,
          onClick: () => { setPrefixDialogOpen(true); },
          tooltip: t.targets.prefixDialog.buttonText,
        },
      ]
    : resumeSetupUrl
      ? [
          {
            key: 'resume',
            label: t.profileDetail.actions.resumeSetup,
            icon: Wand2,
            onClick: () => { void navigate(resumeSetupUrl); },
            tooltip: t.profileDetail.actions.resumeSetup,
          },
        ]
      : [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-semibold">{t.profiles.targetCard.title}</CardTitle>
            <CardDescription>{t.profiles.targetCard.description}</CardDescription>
          </div>
          <CardHeaderActions actions={actions} />
        </div>
      </CardHeader>

      {target && (
        <EditTargetDialog
          orgId={orgId}
          profileId={profileId}
          target={target}
          regionId={regionId}
          onSuccess={onTestSuccess}
          externalOpen={editDialogOpen}
          onExternalOpenChange={setEditDialogOpen}
        />
      )}

      {target && (
        <ChangeTablePrefixDialog
          orgId={orgId}
          profileId={profileId}
          target={target}
          sourceId={source?.id}
          hasTriggeredInitialSync={hasTriggeredInitialSync ?? false}
          open={prefixDialogOpen}
          onOpenChange={setPrefixDialogOpen}
          onSuccess={onTestSuccess}
        />
      )}

      {/* Firewall Configuration Dialog */}
      <FirewallHelpDialog
        open={firewallDialogOpen}
        onOpenChange={setFirewallDialogOpen}
        regionId={regionId}
      />
      <CardContent>
        {!target ? (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>{t.profiles.targetCard.noTargetAlert}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="space-y-3">
              {/* First row: Type, Server, Database, Created */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-3">
                <div className="min-w-0">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.targetCard.type}
                  </label>
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium background-neutral text-neutral rounded-full">
                    {target.$type}
                  </span>
                </div>

                <div className="min-w-0 flex-1">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.targetCard.server}
                  </label>
                  <div className="group flex items-center gap-2">
                    <span className="text-sm break-all">{target.server || t.profiles.targetCard.notAvailable}</span>
                    {target.server && (
                      <button
                        onClick={() => { handleCopy(target.server || '', 'targetServer'); }}
                        className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:background-neutral rounded"
                        title={t.profiles.targetCard.copyServer}
                      >
                        {copiedField === 'targetServer' ? (
                          <Check className="h-4 w-4 text-success" />
                        ) : (
                          <Copy className="h-4 w-4 text-neutral" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                <div className="min-w-0 flex-1">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.targetCard.database}
                  </label>
                  <div className="group flex items-center gap-2">
                    <span className="text-sm break-all">{target.database || t.profiles.targetCard.notAvailable}</span>
                    {target.database && (
                      <button
                        onClick={() => { handleCopy(target.database || '', 'targetDatabase'); }}
                        className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:background-neutral rounded"
                        title={t.profiles.targetCard.copyDatabase}
                      >
                        {copiedField === 'targetDatabase' ? (
                          <Check className="h-4 w-4 text-success" />
                        ) : (
                          <Copy className="h-4 w-4 text-neutral" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                <div className="min-w-0 flex-1">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.targetCard.schema}
                  </label>
                  <div className="group flex items-center gap-2">
                    <span className="text-sm break-all">{target.schemaName || t.profiles.targetCard.notAvailable}</span>
                    {target.schemaName && (
                      <button
                        onClick={() => { handleCopy(target.schemaName || '', 'targetSchema'); }}
                        className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:background-neutral rounded"
                        title={t.profiles.targetCard.copySchema}
                      >
                        {copiedField === 'targetSchema' ? (
                          <Check className="h-4 w-4 text-success" />
                        ) : (
                          <Copy className="h-4 w-4 text-neutral" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                <div className="min-w-0 flex-1">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.targetCard.tablePrefix}
                  </label>
                  <span className="text-sm break-all">
                    {target.tablePrefix ?? t.profiles.targetCard.noTablePrefix}
                  </span>
                </div>
              </div>

              {/* Second row: Connection Status, Last Connection Test, Next Sync, Created */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-3">
                <div className="min-w-0">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.targetCard.connectionStatus}
                  </label>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                      target.connectionStatus === 'Success'
                        ? 'background-success text-success border border-success'
                        : 'background-error text-error border border-error'
                    }`}
                  >
                    {target.connectionStatus === 'Success' ? (
                      <CheckCircle2 className="h-3 w-3" />
                    ) : (
                      <AlertCircle className="h-3 w-3" />
                    )}
                    {target.connectionStatus}
                  </span>
                </div>

                <div className="min-w-0">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.targetCard.lastConnectionTest}
                  </label>
                  <div className="text-sm">
                    {target.connectionLastCheckedUtc
                      ? new Date(target.connectionLastCheckedUtc).toLocaleString()
                      : t.profiles.targetCard.neverTested}
                  </div>
                </div>

                <div className="min-w-0">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.targetCard.nextSync}
                  </label>
                  <div className="text-sm">
                    {isProfileEnabled ? formatNextSyncTime(nextSyncTime) : t.profiles.sync.notScheduledDisabled}
                  </div>
                </div>

                <div className="min-w-0">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.targetCard.created}
                  </label>
                  <div className="text-sm">
                    {target.createdUtc ? new Date(target.createdUtc).toLocaleDateString() : t.profiles.targetCard.notAvailable}
                  </div>
                </div>
              </div>
            </div>

            {connectionTestResult && (
              <ConnectionTestAlert
                success={connectionTestResult.success}
                message={connectionTestResult.message}
              />
            )}

            {agent && (
              <>
              <Separator className="my-6" />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">{t.agents.assignedAgent}</h3>
                {isVersionOutdated(agent.version, latestVersion) && latestVersion && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4 text-warning" />
                    <AlertTitle>{t.agents.updateAvailable}</AlertTitle>
                    <AlertDescription>
                      {t.agents.profileAgentOutdated} (v{latestVersion})
                    </AlertDescription>
                  </Alert>
                )}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-x-4 gap-y-2">
                  <div className="min-w-0">
                    <label className="text-sm font-medium text-muted-foreground block mb-1">
                      {t.agents.table.name}
                    </label>
                    <span className="text-sm">{agent.name}</span>
                  </div>
                  <div className="min-w-0">
                    <label className="text-sm font-medium text-muted-foreground block mb-1">
                      {t.agents.table.status}
                    </label>
                    <Badge variant={agent.status === 'Active' ? 'default' : agent.status === 'Inactive' ? 'destructive' : 'secondary'}>
                      {t.agents.status[agent.status]}
                    </Badge>
                  </div>
                  <div className="min-w-0">
                    <label className="text-sm font-medium text-muted-foreground block mb-1">
                      {t.agents.health.label}
                    </label>
                    <AgentHealthStatus agent={agent} />
                  </div>
                  <div className="min-w-0">
                    <label className="text-sm font-medium text-muted-foreground block mb-1">
                      {t.agents.table.version}
                    </label>
                    <span className="text-sm flex items-center gap-1.5">
                      {agent.version ?? '-'}
                      {isVersionOutdated(agent.version, latestVersion) && (
                        <AlertTriangle className="h-4 w-4 text-warning" />
                      )}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <label className="text-sm font-medium text-muted-foreground block mb-1">
                      {t.agents.table.lastHeartbeat}
                    </label>
                    <span className="text-sm">
                      {agent.lastHeartbeatUtc
                        ? new Date(agent.lastHeartbeatUtc).toLocaleString()
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
